/**
 * Assessment Play Page
 * Container for the assessment player
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AssessmentPlayer } from '../components/AssessmentPlayer';
import { ResultsSummary } from '../components/ResultsSummary';
import { AttemptResults, ScoredResponse } from '../../types/models';
import { attemptsApi } from '../api';

export const AssessmentPlay: React.FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const [results, setResults] = useState<AttemptResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (attemptId) {
      checkAttemptStatus();
    }
  }, [attemptId]);

  const checkAttemptStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get attempt data to check status
      const attemptData = await attemptsApi.getById(attemptId!);
      
      // If attempt is already completed, construct results and show them
      if (attemptData.status === 'completed') {
        const categoryScores = attemptData.category_scores || [];
        
        // Construct results_by_question from responses and questions
        const resultsByQuestion: ScoredResponse[] = attemptData.responses.map((response: any) => {
          const question = attemptData.questions.find((q: any) => q.id === response.question_id);
          return {
            ...response,
            question: question || undefined,
          };
        });

        const attemptResults: AttemptResults = {
          attempt: {
            ...attemptData,
            assessment: attemptData.assessment,
          },
          score_raw: attemptData.score_raw ?? 0,
          score_max: attemptData.score_max ?? 0,
          score_percent: attemptData.score_percent ?? 0,
          passed: attemptData.passed,
          time_spent_seconds: attemptData.time_spent_seconds,
          results_by_question: resultsByQuestion,
          category_scores: categoryScores.length > 0 ? categoryScores : undefined,
        };
        
        setResults(attemptResults);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load attempt');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = (completedResults: AttemptResults) => {
    setResults(completedResults);
  };

  const handleReturn = () => {
    navigate('/assessments');
  };

  if (!attemptId) {
    return <div className="error">Invalid attempt ID</div>;
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  if (results) {
    return (
      <ResultsSummary
        results={results}
        onReturn={handleReturn}
      />
    );
  }

  return <AssessmentPlayer attemptId={attemptId} onComplete={handleComplete} />;
};
